import React, { useMemo } from 'react';
import Tooltip from '@mui/material/Tooltip';
import { i18n_number_of_times_class_has_been_watched } from '@yi/i18n/phrases/i18n_number_of_times_class_has_been_watched';

import { getResponsiveImageAttributes } from '~/src/common/image';
import { useCurrentUser } from '~/src/common/user';
import { AddToCalendarMenu, ContentCardMenu, useLocale, ViewCount } from '~/src/components';
import { AddToCalendarDataProvider } from '~/src/components/ContentCardMenu/AddToCalendarMenu';
import type {
  Content as MediaContent,
  Teacher,
} from '~/src/components/MediaPlayer/media-hero-players/types';

import {
  Container,
  ContextMenuButtonWrapper,
  Image,
  PlayButtonWrapper,
  ViewCountWrapper,
} from './elements';
import { PlayButton } from './PlayButton';

interface Props {
  content: MediaContent & {
    full_url: string;
    media_duration: {
      minutes: number;
    };
    secondary_teacher: Teacher | null;
    snippet: string;
  };
}

export const HeroMedia: React.FC<Props> = ({ content }) => {
  const backgroundImage = content?.no_text_image?.processed_url || '';
  const imageAttributes = getResponsiveImageAttributes(backgroundImage);
  const { locale } = useLocale();
  const { isLoggedIn } = useCurrentUser();

  const seenBy = content.completed_count || 0;

  const practiceDetails = useMemo(
    () => ({
      id: content.id,
      title: content.title,
      full_url: content.full_url,
      media_duration: content.media_duration,
      teacher: content.teacher,
      secondary_teacher: content.secondary_teacher,
      // NOTE snippet in Class/Meditation Page model is equal to description in Class/Mediation Card Model
      description: content.snippet,
    }),
    [content],
  );

  return (
    <AddToCalendarDataProvider practiceDetails={practiceDetails}>
      <Container>
        <Tooltip
          disableInteractive
          placement="right"
          title={i18n_number_of_times_class_has_been_watched(locale)}
        >
          <ViewCountWrapper>
            <ViewCount count={seenBy} />
          </ViewCountWrapper>
        </Tooltip>
        {isLoggedIn && (
          <ContextMenuButtonWrapper>
            <ContentCardMenu contentId={content.id} contentTitle={content.title}>
              <AddToCalendarMenu />
            </ContentCardMenu>
          </ContextMenuButtonWrapper>
        )}
        <PlayButtonWrapper>
          <PlayButton content={content} />
        </PlayButtonWrapper>
        <Image {...imageAttributes} />
      </Container>
    </AddToCalendarDataProvider>
  );
};
